import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/**
 * A search input component that maintains focus during re-renders and handles debounced search
 * 
 * @param {Object} props
 * @param {string} props.value - The current search value
 * @param {Function} props.onChange - Callback when search value changes (debounced)
 * @param {boolean} props.loading - Whether the component is in loading state
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.debounceTime - Debounce time in milliseconds
 */
const SearchInput = ({ 
  value = '', 
  onChange, 
  loading = false,
  placeholder = 'Search...',
  debounceTime = 600
}) => {
  // Internal state to track input value in real time
  const [inputValue, setInputValue] = useState(value);
  
  // Update internal value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  // Create a ref for the input
  const inputRef = useRef(null);
  
  // Track previous loading state to detect when loading finishes
  const [wasLoading, setWasLoading] = useState(loading);
  
  // Effect to re-focus the input when loading state changes from true to false
  useEffect(() => {
    if (wasLoading && !loading) {
      // Focus after a small delay to ensure DOM has updated
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus({
            cursor: 'end', // Place cursor at end of input
          });
        }
      }, 50);
    }
    
    setWasLoading(loading);
  }, [loading, wasLoading]);
  
  // Set up debounce timer reference
  const debounceTimerRef = useRef(null);
  
  // Handle input changes - update internal state immediately
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set a new timer to trigger the onChange after debounce time
    debounceTimerRef.current = setTimeout(() => {
      if (onChange) {
        onChange(newValue);
      }
    }, debounceTime);
  }, [onChange, debounceTime]);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Input
      ref={inputRef}
      placeholder={placeholder}
      value={inputValue}
      onChange={handleChange}
      style={{ marginBottom: 16 }}
      allowClear
      prefix={<SearchOutlined />}
      disabled={loading}
      autoFocus
    />
  );
};

export default SearchInput;
